/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { Select as BaseSelect } from "@base-ui-components/react/select";
import cx from "classnames";
import React from "react";

import ChevronDown from "../../resources/icons/chevron-down.svg";

import "./Select.scss";

type SelectValue = string | number;

export interface OptionProps<VT extends SelectValue = SelectValue> {
  value: VT;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  minprice?: number;
}

interface SelectProps<VT extends SelectValue>
  extends Omit<
    React.HTMLAttributes<HTMLSpanElement>,
    "children" | "defaultValue" | "onChange" | "onSelect"
  > {
  children?: React.ReactNode;
  name?: string;
  value?: VT;
  defaultValue?: VT;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  dropdownClassName?: string;
  dropdownStyle?: React.CSSProperties;
  onChange?: (value: VT, option?: OptionProps<VT>) => void;
  onSelect?: (value: VT, option?: OptionProps<VT>) => void;
}

const isEmptyValue = (value: SelectValue | null | undefined) =>
  value === undefined || value === null || value === "";

const getOptionLabel = (children: React.ReactNode) =>
  React.Children.toArray(children)
    .map((child) =>
      typeof child === "string" || typeof child === "number" ? child : "",
    )
    .join(" ")
    .trim();

export const Option = <VT extends SelectValue>({
  children,
}: OptionProps<VT>) => <>{children}</>;

export const Select = <VT extends SelectValue>({
  children,
  className,
  defaultValue,
  disabled,
  dropdownClassName,
  dropdownStyle,
  id,
  name,
  onChange,
  onSelect,
  placeholder,
  style,
  value,
  ...props
}: SelectProps<VT>) => {
  const [open, setOpen] = React.useState(false);
  const [uncontrolledValue, setUncontrolledValue] = React.useState<VT | null>(
    defaultValue ?? null,
  );
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : uncontrolledValue;
  const options = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => {
      const option = child as React.ReactElement<OptionProps<VT>>;
      const label = option.props.label ?? getOptionLabel(option.props.children);

      return {
        key: option.key ?? String(option.props.value),
        label,
        props: option.props,
        value: option.props.value,
      };
    });
  const items = options.map((option) => ({
    label: option.props.children,
    value: option.value,
  }));
  const selectedOption = options.find((option) =>
    Object.is(option.value, currentValue),
  );
  const hasValue = !isEmptyValue(currentValue);

  const handleValueChange = (nextValue: VT) => {
    const nextOption = options.find((option) =>
      Object.is(option.value, nextValue),
    );

    if (!isControlled) {
      setUncontrolledValue(nextValue);
    }

    onChange?.(nextValue, nextOption?.props);
    onSelect?.(nextValue, nextOption?.props);
  };

  return (
    <span
      className={cx(
        "Select",
        "Select-single",
        "Select-show-arrow",
        open && "Select-open",
        disabled && "Select-disabled",
        className,
      )}
      style={style}
    >
      <BaseSelect.Root<VT>
        id={id}
        name={name}
        value={currentValue as VT}
        items={items}
        disabled={disabled}
        modal={false}
        onOpenChange={setOpen}
        onValueChange={handleValueChange}
      >
        <BaseSelect.Trigger {...props} id={id} className="Select-selector">
          <BaseSelect.Value
            className={
              hasValue
                ? "Select-selection-item"
                : "Select-selection-placeholder"
            }
          >
            {hasValue ? selectedOption?.props.children : placeholder}
          </BaseSelect.Value>
          <BaseSelect.Icon className="Select-arrow" aria-hidden="true">
            <ChevronDown aria-hidden="true" />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner
            className="Select-positioner"
            alignItemWithTrigger={false}
          >
            <BaseSelect.Popup
              className={cx("Select-dropdown", dropdownClassName)}
              style={dropdownStyle}
            >
              {options.map((option) => (
                <BaseSelect.Item
                  key={option.key}
                  value={option.value}
                  disabled={option.props.disabled}
                  className={cx(
                    "Select-item",
                    "Select-item-option",
                    option.props.className,
                  )}
                  style={option.props.style}
                  label={option.label}
                >
                  <BaseSelect.ItemText className="Select-item-option-content">
                    {option.props.children}
                  </BaseSelect.ItemText>
                </BaseSelect.Item>
              ))}
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    </span>
  );
};
